<template>
  <section class="login-information">
    <!-- お知らせ -->
    <section class="section">
      <h3 class="title" v-t="'appLoginInformation.newsFromIField'" />
      <ul class="list-news" v-bind:style="{ '--item-count': newsCount }">
        <li
          v-for="(news, index) in targetNewses"
          v-bind:key="index"
          class="item"
        >
          <div v-if="news.isLoading" class="loading" />
          <template v-else>
            <span class="date">{{ news.pubDate }}</span>
            <a
              v-bind:href="news.link"
              target="_blank"
              rel="noopener noreferrer"
              class="link news"
            >
              <span class="text">{{ news.title }}</span>
              <icon-link class="icon" />
            </a>
          </template>
        </li>
      </ul>
      <div class="news-other">
        <a
          href="https://www.multisoup.co.jp/support_news/"
          target="_blank"
          rel="noopener noreferrer"
          class="link news-other"
        >
          <span class="text" v-t="'appLoginInformation.more'" />
          <icon-link class="icon small" />
        </a>
      </div>
    </section>
    <!-- ヘルプ -->
    <section class="section">
      <h3 class="title" v-t="'appLoginInformation.help'" />
      <ul class="list-help">
        <li class="item">
          <span class="text">
            {{ $t('appLoginInformation.manual') }}
          </span>
          <span class="text separator">:</span>
          <a
            href="https://www.multisoup.co.jp/support/"
            target="_blank"
            rel="noopener noreferrer"
            class="link help"
          >
            <span class="text" v-t="'appLoginInformation.userSupport'" />
            <icon-link class="icon" />
          </a>
        </li>
      </ul>
    </section>
  </section>
</template>

<script>
import moment from 'moment'
import IconLink from '@/assets/ifield/external-link.svg?component'

// RSS取得先URL
const RSS_URL = 'https://www.multisoup.co.jp/support_news/feed/'
// 日付表示フォーマット
const DATETIME_DISPLAY_FORMAT = 'YYYY/MM/DD'

export default {
  name: 'AppLoginInformation',
  components: {
    'icon-link': IconLink,
  },
  props: {
    /**
     * お知らせ表示件数
     */
    newsCount: {
      type: Number,
      default: 3,
    },
  },
  data: function () {
    return {
      newses: [],
    }
  },
  computed: {
    /**
     * 表示対象のお知らせ
     * @type {Array.<Object>}
     */
    targetNewses: function () {
      if (!this.newses.length) {
        return [...new Array(3)].map(() => ({ isLoading: true }))
      }
      return this.newses.slice(0, Math.min(this.newsCount, this.newses.length))
    },
  },
  mounted: function () {
    this.$_readManagementNewses()
  },
  methods: {
    /**
     * RSSからお知らせを受信する
     */
    $_fetchRSS: async function () {
      // データを受信してRSS内にあるitemを取得
      const response = await fetch(RSS_URL)
      const text = await response.text()
      const xml = await new window.DOMParser().parseFromString(text, 'text/xml')
      this.newses = [...xml.querySelectorAll('item')].map((item) => ({
        // お知らせの表示に必要なデータを取り出す
        title: item.querySelector('title').innerHTML,
        link: item.querySelector('link').innerHTML,
        pubDate: moment(item.querySelector('pubDate').innerHTML).format(
          DATETIME_DISPLAY_FORMAT
        ),
      }))
    },

    /**
     * 運営からのお知らせを読み取る
     */
    $_readManagementNewses: async function () {
      try {
        await this.$_fetchRSS()
      } catch (error) {
        this.$log.error('unexpected error', error)
      }
    },
  },
}
</script>

<style lang="scss" scoped>
$-item-height: 1.5rem;
$-text-color: $text-color;

.section {
  width: auto;
  min-width: 31.5rem;
  margin-bottom: 32px;
  text-align: left;
  color: $-text-color;

  .title {
    font-size: $font-size-xlarge;
    font-weight: $font-weight-bold;
    line-height: 1;
    padding: 0 0 16px 0;
    margin: 0 0 16px 0;
    border-bottom: solid 1px $-text-color;
  }
}

// スケルトンローディング
.loading {
  position: relative;
  height: $-item-height;
  width: 100%;
  display: inline-block;
  overflow: hidden;
  background-color: #e2e2e2;
  border-radius: 4px;

  @keyframes skeleton-animation {
    0% {
      transform: translateX(-100%);
    }
    100% {
      transform: translateX(100%);
    }
  }

  &::before {
    position: absolute;
    content: '';
    display: block;
    height: 100%;
    width: 100%;
    background: linear-gradient(
      90deg,
      transparent,
      rgba(255, 255, 255, 0.2),
      transparent
    );
    animation: skeleton-animation 1.2s linear infinite;
  }
}

.item {
  height: $-item-height;
  width: 100%;
  display: inline-flex;
  justify-content: flex-start;
  align-items: center;
  line-height: 1.5;

  // リストの先頭記号をカスタマイズ
  list-style-type: none;

  &::before {
    position: relative;
    left: -1rem;
    display: inline-block;
    width: 0.25rem;
    height: 0.25rem;
    content: '';
    border-radius: 100%;
    background: $-text-color;
  }
}

.date {
  display: inline-block;
  width: 7rem;
}

.link {
  color: $-text-color;
  text-decoration: underline;
  display: inline-flex;
  justify-content: flex-start;
  align-items: flex-end;
  column-gap: 2px;

  &.help {
    color: #1ca570;
  }

  &.news-other {
    color: #1ca570;

    .text {
      font-size: $font-size-middle;
    }
  }
}

.text {
  font-size: $font-size-large;
  max-width: 21rem;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;

  &.separator {
    position: relative;
    top: -2px;
    display: inline-block;
    width: $font-size-large;
    text-align: center;
  }
}

.news-other {
  width: 100%;
  display: inline-flex;
  justify-content: flex-end;
  align-items: center;
}

.icon {
  display: inline-block;
  margin: auto 0;
  width: 1.125rem;
  height: 1.125rem;
  fill: $-text-color;

  &.small {
    width: 1rem;
    height: 1rem;
  }
}
</style>
